import { useEffect, useRef } from "react";
import type { ViewNavSource } from "../../shared/mutators/viewAnalytics";

type View = {
  visitId: string;
  navSource: ViewNavSource;
  priorTab: string | null;
};

type Props = {
  visitKey: string | null;
  tab: string | null;
  onView: (view: View) => void;
};

/**
 * Emits a view when `tab` changes within a visit. A visit lasts while
 * `visitKey` stays the same; a null key or tab records nothing.
 */
export function useViewVisit({ visitKey, tab, onView }: Props) {
  const onViewRef = useRef(onView);
  const visit = useRef<{ id: string; key: string; tab: string | null } | null>(
    null
  );

  useEffect(() => {
    onViewRef.current = onView;
  });

  useEffect(() => {
    if (visitKey === null || tab === null) return;
    if (visit.current?.key !== visitKey) {
      visit.current = { id: crypto.randomUUID(), key: visitKey, tab: null };
    }
    const current = visit.current;
    if (current.tab === tab) return;
    const priorTab = current.tab;
    current.tab = tab;
    onViewRef.current({
      visitId: current.id,
      navSource: priorTab === null ? "nav" : "tab_switch",
      priorTab,
    });
  }, [visitKey, tab]);
}
